import PersonRounded from '@mui/icons-material/PersonRounded';
import React from 'react';
import {
  ArrayField,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  EditButton,
  EmailField,
  ExportButton,
  Filter,
  FormTab,
  List,
  NumberInput,
  ReferenceField,
  ReferenceManyField,
  Resource,
  SearchInput,
  SelectArrayInput,
  SelectInput,
  Show,
  ShowButton,
  SingleFieldList,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  UrlField,
  downloadCSV,
  useRecordContext,
} from 'react-admin';
import AmountField from 'shared-fields/AmountField';

import { Divider, Stack } from '@mui/material';
import { ORG_ID } from 'config';
import Role from 'constants/role';
import { useHasRole } from 'hooks/useHasRole';
import jsonExport from 'jsonexport/dist';
import { CopyField } from 'shared-fields/CopyField';
import OrganizationReferenceField from 'shared-fields/OrganizationReferenceField';
import OrganizationReferenceInput from 'shared-fields/OrganizationReferenceInput';
import { getShebaBankName } from 'utils/digit';
import { rolesMatch } from 'utils/user';
import { ActivitiesListContent } from 'views/activities';
import { ChannelListContent } from 'views/channel';
import { WalletTransactionListContent } from 'views/Transaction';
import BankNameField from './BankNameField';
import DownloadContractButton from './Contract/DownloadContractButton';
import DownloadDocButton from './DownloadDocButton';
import GoogleAuthButton from './GoogleAuthorizeButton';
import LoginAsButton from './LoginAsButton';

const exporter = (users) => {
  const usersForExport = users.map((user) => {
    const {
      information: {
        nationalCode, address, postalCode, firstName, lastName,
      } = {}, bankAccount, channels: [{ title: channelTitle, id: channelId, url: channelUrl } = {}] = [{}], ...rest
    } = user;
    return {
      ...rest,
      nationalCode: nationalCode?.toString?.()?.padStart(10, '0'),
      firstName,
      lastName,
      ...bankAccount,
      bankName: getShebaBankName(bankAccount.sheba) || bankAccount.bankName,
      address,
      postalCode,
      channelTitle,
      channelId,
      channelUrl,
    };
  });
  jsonExport(usersForExport, {
    headers: ['id', 'name', 'firstName', 'lastName', 'email', 'gbpCredit', 'sheba', 'cardNumber', 'nationalCode', 'channelId', 'channelTitle', 'channelUrl'], // order fields in the export
  }, (err, csv) => {
    downloadCSV(csv, 'users');
  });
};
function UserTitle() {
  const record = useRecordContext();
  return (
    <span>
      {record ? `${record.id} | ${record.information?.firstName || ''} ${record.information?.lastName || ''}` : ''}
    </span>
  );
}

function UserFilter(props) {
  return (
    <Filter {...props}>
      <SearchInput source="searchQuery" alwaysOn />
      <SelectInput label="Role" source="role" choices={rolesChoices()} alwaysOn />
      <SelectInput source="preferredPayment" label="Preferred Payment" choices={PreferredPaymentChoices} alwaysOn />
      {!ORG_ID && <OrganizationReferenceInput alwaysOn />}
    </Filter>
  );
}

export function UserList(props) {
  const hasRole = useHasRole();
  return (
    <List
      sort={{ field: 'id', order: 'DESC' }}
      filters={<UserFilter />}
      filterDefaultValues={{
      }}
      perPage={25}
      exporter={exporter}
      actions={(
        <TopToolbar>
          {hasRole(Role.ORG_SUPER_ADMIN) && <ExportButton />}
          {hasRole(Role.USER_CREATE) && <CreateButton />}
          {/* <FilterButton filters={<UserFilter />} /> */}
        </TopToolbar>
      )}
      {...props}
    >
      <Datagrid bulkActionButtons={false}>
        <CopyField source="id" label="ID" />
        <CopyField source="email" label="Email" />
        <CopyField source="phoneNumber" label="Phone" />
        <CopyField
          source="information.firstName"
          sx={{ textAlign: 'right', justifyContent: 'end' }}
          label="FirstName"
        />
        <CopyField
          source="information.lastName"
          sx={{ textAlign: 'right', justifyContent: 'end' }}
          label="LastName"
        />
        <AmountField source="gbpCredit" />
        <TextField source="preferredPayment" label="Preferred Payment" />
        {!ORG_ID && <OrganizationReferenceField />}
        {hasRole(Role.CHANNEL_VIEW) && (
          <ArrayField source="channels">
            <SingleFieldList linkType={false}>
              <ReferenceField label="Channel" reference="channels" source="id" />
            </SingleFieldList>
          </ArrayField>
        )}
        {hasRole(Role.ORG_SUPER_ADMIN) && <LoginAsButton />}
        {hasRole(Role.USER_EDIT) && <EditButton />}
        <ShowButton />
      </Datagrid>
    </List>
  );
}

export function UserEdit(props) {
  const hasRole = useHasRole();
  return (
    <Edit mutationMode="optimistic" title={<UserTitle />} {...props}>
      <TabbedForm>
        <FormTab label="General">
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <TextInput source="id" disabled />
            <TextInput source="information.firstName" label="First Name" />
            <TextInput source="information.lastName" label="Last Name" />
          </Stack>

          <TextInput source="email" />
          <TextInput source="telegramUrl" label="Telegram" />

          <NumberInput source="usdCredit" label="USD Credit" disabled />
          <NumberInput source="gbpCredit" label="GBP Credit" disabled />
          {!ORG_ID && <OrganizationReferenceInput />}
          <GoogleAuthButton />
          {
            hasRole(Role.ORG_SUPER_ADMIN) && (
              <SelectArrayInput source="roles" choices={rolesChoices()} />
            )
          }
          {
            hasRole(Role.SUPER_ADMIN) && (
              <TextInput source="password" label="Password" />
            )
          }
        </FormTab>

        {
          hasRole(Role.WALLET_TRANSACTION_VIEW) && (
            <FormTab label="Transactions" path="transactions">
              <TransactionTabContent />
            </FormTab>
          )
        }
        {
          hasRole(Role.CHANNEL_VIEW) && (
            <FormTab label="Channels" path="channels">
              <ChannelTabContent />
            </FormTab>
          )
        }

        <FormTab label="information" path="information">
          <TextInput source="phoneNumber" />
          <TextInput source="information.firstName" label="First Name" />
          <TextInput source="information.lastName" label="Last Name" />
          <TextInput source="information.firstNameEnglish" label="First Name English" />
          <TextInput source="information.lastNameEnglish" label="Last Name English" />
          <TextInput source="information.nationalCode" label="National Code" />
          <TextInput source="information.address" label="Address" multiline />
          <TextInput source="information.postalCode" label="Postal Code" />
          <TextInput source="information.fatherName" label="Father's name" />
          <TextInput source="information.birthDate" label="Birth Date" />
        </FormTab>
        <FormTab label="Financial" path="financial">
          <SelectInput
            source="preferredPayment"
            label="Preferred Payment"
            choices={PreferredPaymentChoices}
            disabled={!hasRole(Role.FINANCIAL_EDIT)}
          />
          <TextInput
            source="preferredPaymentGateway"
            label="Preferred Payment Gateway"
            disabled={!hasRole(Role.FINANCIAL_EDIT)}
          />
          <TextInput
            source="bankAccount.sheba"
            label="Sheba"
            disabled={!hasRole(Role.FINANCIAL_EDIT)}
          />
          <TextInput
            source="bankAccount.cardNumber"
            label="Card Number"
            disabled={!hasRole(Role.FINANCIAL_EDIT)}
          />
          <BankNameField />
          <Divider sx={{ mb: 5 }} />
          <TextInput
            source="bankAccount.TRC20WalletAddress"
            label="TRC20 Wallet"
            sx={{ width: 350 }}
            disabled={!hasRole(Role.FINANCIAL_MANAGEMENT)}
          />
          <TextInput
            source="bankAccount.BEP20WalletAddress"
            label="BEP20 Wallet"
            sx={{ width: 350 }}
            disabled={!hasRole(Role.FINANCIAL_MANAGEMENT)}
          />
          <TextInput
            source="bankAccount.ERC20WalletAddress"
            label="ERC20 Wallet"
            sx={{ width: 350 }}
            disabled={!hasRole(Role.FINANCIAL_MANAGEMENT)}
          />
        </FormTab>
        {
          hasRole(Role.ORG_SUPER_ADMIN) && (
            <FormTab label="history" path="history">
              <ActivityTabContent />
            </FormTab>
          )
        }
      </TabbedForm>

    </Edit>
  );
}

function TransactionTabContent(props) {
  return (
    <ReferenceManyField
      {...props}
      perPage={100}
      reference="transactions"
      target="userId"
      addLabel={false}
    >
      <WalletTransactionListContent />
    </ReferenceManyField>
  );
}
function ChannelTabContent(props) {
  return (
    <ReferenceManyField
      {...props}
      perPage={100}
      reference="channels"
      target="userId"
      addLabel={false}
    >
      <ChannelListContent />
    </ReferenceManyField>
  );
}
function ActivityTabContent(props) {
  return (
    <ReferenceManyField
      {...props}
      perPage={100}
      reference="activities"
      target="recordId"
      addLabel={false}
      filter={{ recordModel: 'User' }}
    >
      <ActivitiesListContent />
    </ReferenceManyField>
  );
}

export function UserShow(props) {
  const hasRole = useHasRole();
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="general">
          <TextField source="information.firstName" />
          <TextField source="information.lastName" />
          <TextField source="name" label="name(deprecated)" />
          <CopyField source="email" />
          <UrlField source="telegramUrl" label="Telegram" />
          <AmountField source="gbpCredit" />
          {!ORG_ID && <OrganizationReferenceField />}
          <GoogleAuthButton />
          <Divider>Docs</Divider>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <DownloadDocButton title="National Card Front" type="nationalCardImage" />
            <DownloadDocButton title="National Card Back" type="nationalCardBackImage" />
            <DownloadContractButton />
          </Stack>

        </Tab>
        {
          hasRole(Role.WALLET_TRANSACTION_VIEW) && (
            <Tab label="Transactions">
              <TransactionTabContent />
            </Tab>
          )
        }
        {
          hasRole(Role.CHANNEL_VIEW) && (
            <Tab label="Channels">
              <ChannelTabContent />
            </Tab>
          )
        }
        <Tab label="Information">
          <TextField source="phoneNumber" label="Phone Number" />
          <TextField source="information.nationalCode" label="National Code" />
          <TextField source="information.address" label="Address" />
          <TextField source="information.postalCode" label="Postal Code" />
          <TextField source="information.fatherName" label="Father Name" />
          <TextField source="information.birthDate" label="Birth Date" disabled />
        </Tab>
        <Tab label="Financial">
          <TextField source="bankAccount.sheba" label="Sheba" />
          <TextField source="bankAccount.cardNumber" label="Card Number" />
          <BankNameField />
          <TextField source="preferredPayment" label="Preferred Payment" />
          <TextField source="preferredPaymentGateway" label="Preferred Payment Gateway" />
          <CopyField
            source="bankAccount.TRC20WalletAddress"
            label="TRC20 Wallet"
            sx={{ width: 350 }}
          />
          <CopyField
            source="bankAccount.ERC20WalletAddress"
            label="TRC20 Wallet"
            sx={{ width: 350 }}
          />
          <CopyField
            source="bankAccount.BEP20WalletAddress"
            label="TRC20 Wallet"
            sx={{ width: 350 }}
          />
        </Tab>
        {
          hasRole(Role.ORG_SUPER_ADMIN) && (
            <Tab label="history">
              <ActivityTabContent />
            </Tab>
          )
        }
      </TabbedShowLayout>
    </Show>
  );
}

function rolesChoices() {
  return Object.entries(Role).map((role) => ({
    id: role[0],
    name: role[1],

  })).filter((role) => role.id !== Role.SUPER_ADMIN);
}

const PreferredPaymentChoices = [
  'IRT_PAYMENT', 'USDT_PAYMENT', 'GBP_DEPOSIT',
].map((p) => ({ id: p, name: p }));

const PreferredPaymentGatewayChoices = [
  'OM', 'FX', 'KZ',
].map((p) => ({ id: p, name: p }));

export function UserCreate(props) {
  return (
    <Create title="Create User" {...props}>
      <TabbedForm>
        <FormTab label="General">
          <TextInput source="firstName" label="First Name" />
          <TextInput source="lastName" label="Last Name" />
          <TextInput source="email" />
          <TextInput source="phoneNumber" />
          {!ORG_ID && <OrganizationReferenceInput />}
        </FormTab>
      </TabbedForm>
    </Create>
  );
}

export function renderUserResource(roles) {
  return (
    <Resource
      name="users"
      icon={PersonRounded}
      list={UserList}
      edit={rolesMatch({ roles, role: Role.USER_EDIT }) && UserEdit}
      show={UserShow}
      create={rolesMatch({ roles, role: Role.USER_CREATE }) && UserCreate}
    />
  );
}

export const UserIcon = PersonRounded;
